import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import {
	faCalendarCheck,
	faChartPieSimple,
	faChartTreeMap,
	faFolderOpen,
	faGear,
	faGridDividers,
	faHeartPulse,
	faHouse,
	faMessages,
	faPaperPlane,
	faPenField,
	faRankingStar,
	faRectangleList,
	faUsers,
} from "@performyard/fontawesome/pro-light-svg-icons";
import type { SideNavItem } from "@performyard/py-components";

import type { NavigationConfig, SideNavArea, SideNavTag } from "../../api/services/defs/navigation";

type LeafItem = Extract<SideNavItem, { type: "item" }>;

export type SideNavContext = {
	theUserId: string;
};

export type SideNavRegistryItem = {
	id: string;
	label: string;
	href: string | ((context: SideNavContext) => string);
	icon?: IconDefinition;
	fullPageLoad?: boolean;
	visibility?: {
		area: SideNavArea;
		tag?: SideNavTag;
	};
};

export const FEEDBACK_VIEW_ORDER = ["received", "given", "team", "notes", "requests", "outgoing"] as const;
export const ENGAGEMENT_SUBNAV_ORDER = ["overview", "cohorts", "factors", "questions", "enps"] as const;

const ACTIVE_FILTERS_QUERY = `filters=${encodeURIComponent('{"status":["active"]}')}`;

export const SIDE_NAV_REGISTRY = {
	dashboard: {
		id: "dashboard",
		label: "Dashboard",
		href: "/",
		icon: faHouse,
	},
	myGoals: {
		id: "my-goals",
		label: "My Goals",
		href: ({ theUserId }) => `/employees/${theUserId}/goals/mygoals/?${ACTIVE_FILTERS_QUERY}`,
		visibility: { area: "performance", tag: "goals" },
	},
	myTeamGoals: {
		id: "my-team-goals",
		label: "My Team's Goals",
		href: ({ theUserId }) => `/employees/${theUserId}/goals/teamGoals/?${ACTIVE_FILTERS_QUERY}&pageNumber=1`,
		visibility: { area: "performance", tag: "goals" },
	},
	companyGoalsList: {
		id: "list",
		label: "Company Goals",
		href: "/company/goals/list?ownership=team&name=end_date&direction=desc",
		visibility: { area: "goals", tag: "list" },
	},
	companyGoalsOverview: {
		id: "overview",
		label: "Company Goals Overview",
		href: "/company/goals/dashboard",
		fullPageLoad: true,
		visibility: { area: "goals", tag: "dashboard" },
	},
	companyGoalsAlignment: {
		id: "alignment",
		label: "Company Goals Alignment",
		href: "/company/goals/alignment",
		visibility: { area: "goals", tag: "alignment" },
	},
	employeesWithoutGoals: {
		id: "employees-without-goals",
		label: "Employees Without Goals",
		href: "/company/goals/employees-without-goals?pageNumber=1&name=name&direction=asc",
		visibility: { area: "goals", tag: "employees-without-goals" },
	},
	formsINeedToDo: {
		id: "forms-i-need-to-do",
		label: "Forms I Need To Do",
		href: ({ theUserId }) => `/reviews/${theUserId}/forms-i-need-to-do/?name=authors_due_date&direction=asc`,
		visibility: { area: "reviews", tag: "forms-i-need-to-do" },
	},
	formsIDid: {
		id: "forms-i-did",
		label: "Forms I Did",
		href: ({ theUserId }) => `/reviews/${theUserId}/forms-i-did/?name=complete_date&direction=desc`,
		visibility: { area: "reviews", tag: "forms-i-did" },
	},
	reviewsOfMe: {
		id: "reviews-of-me",
		label: "Reviews of Me",
		href: ({ theUserId }) => `/reviews/${theUserId}/reviews-of-me/?name=start_date&direction=desc`,
		visibility: { area: "reviews", tag: "reviews-of-me" },
	},
	reviewsOfMyTeam: {
		id: "reviews-of-my-team",
		label: "My Team's Reviews",
		href: ({ theUserId }) => `/reviews/${theUserId}/reviews-of-my-team/?name=start_date&direction=desc`,
		visibility: { area: "reviews", tag: "reviews-of-my-team" },
	},
	reviewCycles: {
		id: "review-cycles",
		label: "Review Cycles",
		href: "/company/reviews/cycles/?name=start_date&direction=desc",
		visibility: { area: "reviews", tag: "review-cycles" },
	},
	allReviews: {
		id: "all-reviews",
		label: "Company Reviews",
		href: "/company/reviews/all/?name=start_date&direction=desc",
		visibility: { area: "reviews", tag: "all-reviews" },
	},
	allForms: {
		id: "all-forms",
		label: "Company Forms",
		href: "/company/reviews/form-report/?name=authors_due_date&direction=desc",
		visibility: { area: "reviews", tag: "all-forms" },
	},
	assignments: {
		id: "assignments",
		label: "Review Assignments",
		href: "/company/reviews/overview/",
		visibility: { area: "reviews", tag: "assignments" },
	},
	performanceGraphs: {
		id: "performance-graphs",
		label: "Performance Graphs",
		href: "/company/reviews/graphs/",
		fullPageLoad: true,
		visibility: { area: "reviews", tag: "performance-graphs" },
	},
	feedbackReceived: {
		id: "feedback-received",
		label: "Feedback About Me",
		href: ({ theUserId }) => `/feedback/${theUserId}/received`,
		visibility: { area: "feedback", tag: "received" },
	},
	feedbackGiven: {
		id: "feedback-given",
		label: "Feedback I've Given",
		href: ({ theUserId }) => `/feedback/${theUserId}/given`,
		visibility: { area: "feedback", tag: "given" },
	},
	feedbackTeam: {
		id: "feedback-team",
		label: "My Team's Feedback",
		href: ({ theUserId }) => `/feedback/${theUserId}/team`,
		visibility: { area: "feedback", tag: "team" },
	},
	feedbackNotes: {
		id: "feedback-notes",
		label: "My Notes",
		href: ({ theUserId }) => `/feedback/${theUserId}/notes`,
		visibility: { area: "feedback", tag: "notes" },
	},
	feedbackRequests: {
		id: "feedback-requests",
		label: "Incoming Feedback Requests",
		href: ({ theUserId }) => `/feedback/${theUserId}/requests`,
		visibility: { area: "feedback", tag: "requests" },
	},
	feedbackOutgoing: {
		id: "feedback-outgoing",
		label: "Outgoing Feedback Requests",
		href: ({ theUserId }) => `/feedback/${theUserId}/outgoing`,
		visibility: { area: "feedback", tag: "outgoing" },
	},
	allFeedback: {
		id: "all-feedback",
		label: "All Feedback",
		href: "/company/feedback/",
		visibility: { area: "company", tag: "feedback" },
	},
	workspaces: {
		id: "workspaces",
		label: "My Meeting Workspaces",
		href: "/meeting-workspaces/",
		visibility: { area: "meeting-workspaces" },
	},
	actionItems: {
		id: "action-items",
		label: "My Action Items",
		href: "/meeting-workspaces/?tab=action-items",
		visibility: { area: "meeting-workspaces" },
	},
	allMeetings: {
		id: "all-meetings",
		label: "Company Meetings",
		href: "/company/meetings?name=meetingDate&direction=desc",
		visibility: { area: "company", tag: "meetings" },
	},
	allWorkflows: {
		id: "all-workflows",
		label: "All Workflows",
		href: "/company/workflows",
		visibility: { area: "company", tag: "workflows" },
	},
	allTodos: {
		id: "all-todos",
		label: "All To-Do's",
		href: "/todos",
		fullPageLoad: true,
		visibility: { area: "company", tag: "workflows" },
	},
	myDocuments: {
		id: "my-documents",
		label: "My Documents",
		href: ({ theUserId }) => `/documents/${theUserId}`,
		visibility: { area: "performance", tag: "documents" },
	},
	companyDocuments: {
		id: "company-documents",
		label: "Company Documents",
		href: "/company/resources/documents",
		visibility: { area: "company", tag: "resources" },
	},
	companyLinks: {
		id: "company-links",
		label: "Company Links",
		href: "/company/resources/links",
		visibility: { area: "company", tag: "resources" },
	},
	engagementOverview: {
		id: "engagement-overview",
		label: "Overview",
		href: "/engagement/overview/",
		visibility: { area: "engagement", tag: "overview" },
	},
	engagementCohorts: {
		id: "engagement-cohorts",
		label: "Cohorts",
		href: "/engagement/cohorts/",
		visibility: { area: "engagement", tag: "cohorts" },
	},
	engagementFactors: {
		id: "engagement-factors",
		label: "Factors",
		href: "/engagement/factors/",
		visibility: { area: "engagement", tag: "factors" },
	},
	engagementQuestions: {
		id: "engagement-questions",
		label: "Questions",
		href: "/engagement/questions/",
		visibility: { area: "engagement", tag: "questions" },
	},
	engagementEnps: {
		id: "engagement-enps",
		label: "eNPS",
		href: "/engagement/enps/",
		visibility: { area: "engagement", tag: "enps" },
	},
	directory: {
		id: "directory",
		label: "Directory",
		href: "/directory",
		icon: faUsers,
		visibility: { area: "company", tag: "directory" },
	},
	orgChart: {
		id: "org-chart",
		label: "Org-Chart",
		href: "/org-chart",
		icon: faGridDividers,
		fullPageLoad: true,
		visibility: { area: "company", tag: "org-chart" },
	},
	surveys: {
		id: "surveys",
		label: "Surveys",
		href: "/surveys/",
		icon: faPaperPlane,
		visibility: { area: "surveys" },
	},
	compensation: {
		id: "compensation",
		label: "Compensation",
		href: "/compensation/",
		icon: faRankingStar,
		visibility: { area: "compensation" },
	},
	analytics: {
		id: "analytics",
		label: "Reporting & Analytics",
		href: "/analytics/",
		icon: faRectangleList,
		visibility: { area: "analytics" },
	},
	companySettings: {
		id: "company-settings",
		label: "Company Settings",
		href: "/administration/",
		icon: faGear,
		fullPageLoad: true,
		visibility: { area: "administration" },
	},
} as const satisfies Record<string, SideNavRegistryItem>;

export const SIDE_NAV_GROUPS = {
	goals: { id: "goals", label: "Goals", icon: faChartPieSimple },
	reviews: { id: "reviews", label: "Reviews", icon: faPenField },
	feedback: { id: "feedback", label: "Feedback", icon: faMessages },
	meetings: { id: "meetings", label: "Meetings", icon: faCalendarCheck },
	workflows: { id: "workflows", label: "Workflows", icon: faChartTreeMap },
	documents: { id: "resources", label: "Resources", icon: faFolderOpen },
	engagement: { id: "engagement", label: "Engagement", icon: faHeartPulse },
} as const;

/**
 * Detail routes that sit under one nav item's path but should highlight a different item when
 * the URL carries an originating `source` (the meetings breadcrumb source param). Lets the
 * generic active-key resolver honor where the user came from without coupling it to routes.
 */
export const SIDE_NAV_SOURCE_ACTIVE_RULES = [
	{ source: "company-meetings", pathPrefix: "/meeting-workspaces", itemId: SIDE_NAV_REGISTRY.allMeetings.id },
	{ source: "action-items", pathPrefix: "/meeting-workspaces", itemId: SIDE_NAV_REGISTRY.actionItems.id },
] as const;

export const SIDE_NAV_SERVER_TAG_REQUIREMENTS = (Object.values(SIDE_NAV_REGISTRY) as SideNavRegistryItem[]).flatMap(
	(item) => (item.visibility ? [item.visibility] : [])
);

export const hasVisibleTag = (
	config: Pick<NavigationConfig, "visibleNavTags">,
	area: SideNavArea,
	tag?: SideNavTag
): boolean => {
	const tagsForArea = config.visibleNavTags?.[area];
	if (tagsForArea === undefined) return false;
	if (tag === undefined) return true;
	return tagsForArea.includes(tag);
};

export const resolveSideNavRegistryItem = (item: SideNavRegistryItem, context: SideNavContext): LeafItem => ({
	id: item.id,
	type: "item",
	label: item.label,
	href: typeof item.href === "function" ? item.href(context) : item.href,
	icon: item.icon,
	fullPageLoad: item.fullPageLoad,
});
